import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Sidebar } from "@/components/Sidebar";
import { ChatMessage } from "@/components/ChatMessage";
import { ChatInput } from "@/components/ChatInput";
import { ModelPicker } from "@/components/ModelPicker";
import { Menu, Sparkles, Globe, Bot, Coins, Share2, FileText, Video } from "lucide-react";
import { toast } from "sonner";
import { OnboardingTour } from "@/components/OnboardingTour";
import { ASSETS } from "@/lib/assets";

const SUGGESTIONS = [
  { icon: Globe, title: "Build a landing page", prompt: "Build me a responsive landing page for a SaaS product using React and Tailwind CSS." },
  { icon: FileText, title: "Make a PDF", prompt: "Write a formal one-page leave request letter to my manager and give it to me as a PDF I can download and print." },
  { icon: Bot, title: "Create an AI agent", prompt: "Show me how to build a simple AI agent in Python that can use tools to answer questions." },
  { icon: Sparkles, title: "Generate an image", prompt: "Create an image of a modern minimalist logo for a coffee brand called 'Brewly'." },
  { icon: Video, title: "Generate a video", prompt: "Generate a short cinematic video of a neon city skyline at night with flying cars." },
];

export default function Chat() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [models, setModels] = useState([]);
  const [model, setModel] = useState("gemini-3.1-pro-preview");
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const [streaming, setStreaming] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (user && user.id) {
      const key = `nigamai_onboarded_${user.id}`;
      if (!localStorage.getItem(key)) setShowTour(true);
    }
  }, [user]);

  const closeTour = () => {
    if (user?.id) localStorage.setItem(`nigamai_onboarded_${user.id}`, "1");
    setShowTour(false);
  };

  const shareChat = async () => {
    if (!activeId) return;
    try {
      const { data } = await api.post(`/conversations/${activeId}/share`);
      const url = `${window.location.origin}/share/${data.share_id}`;
      await navigator.clipboard.writeText(url);
      toast.success("Public share link copied to clipboard!");
    } catch {
      toast.error("Could not create share link");
    }
  };

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
    loadConversations();
    api.post("/credits/daily").then(({ data }) => {
      if (data.claimed) {
        const msg = data.streak > 1
          ? `🔥 Day ${data.streak} streak! +${data.amount} free credits`
          : `🎁 +${data.amount} free daily credits — welcome back!`;
        toast.success(msg);
        refreshUser();
      }
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const loadConversations = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch {}
  };

  const openConversation = async (id) => {
    setActiveId(id);
    setSidebarOpen(false);
    try {
      const { data } = await api.get(`/conversations/${id}`);
      setMessages(data.messages);
      if (data.model) setModel(data.model);
    } catch {
      toast.error("Failed to load conversation");
    }
  };

  const newChat = () => {
    setActiveId(null);
    setMessages([]);
    setSidebarOpen(false);
  };

  const deleteConversation = async (id) => {
    try {
      await api.delete(`/conversations/${id}`);
      setConversations((prev) => prev.filter((c) => c.id !== id));
      if (activeId === id) newChat();
      toast.success("Conversation deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const renameConversation = async (id, title) => {
    try {
      await api.patch(`/conversations/${id}`, { title });
      setConversations((prev) => prev.map((c) => (c.id === id ? { ...c, title } : c)));
      toast.success("Renamed");
    } catch {
      toast.error("Failed to rename");
    }
  };

  const togglePin = async (id, pinned) => {
    try {
      await api.patch(`/conversations/${id}`, { pinned });
      loadConversations();
      toast.success(pinned ? "Pinned to top" : "Unpinned");
    } catch {
      toast.error("Failed to update");
    }
  };

  const pollForVideo = (convoId) => {
    let tries = 0;
    const maxTries = 120; // ~10 min at 5s
    const interval = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get(`/conversations/${convoId}`);
        setMessages(data.messages);
        const last = data.messages[data.messages.length - 1];
        const stillPending = last && last.role === "assistant" && last.content.includes("<!--VPEND-->");
        if (!stillPending || tries >= maxTries) {
          clearInterval(interval);
          refreshUser();
        }
      } catch {
        if (tries >= maxTries) clearInterval(interval);
      }
    }, 5000);
  };

  const sendMessage = async (text, images, docIds = [], videoOptions = null, meta = {}) => {
    setBusy(true);
    setStreaming(true);
    const userMsg = { role: "user", content: text, images, doc_names: meta.docNames || [] };
    setMessages((prev) => [...prev, userMsg, { role: "assistant", content: "", images: [] }]);

    let convoId = activeId;
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ conversation_id: activeId, message: text, model, images, doc_ids: docIds, video_options: videoOptions }),
      });

      if (res.status === 402) {
        setMessages((prev) => prev.slice(0, -2));
        toast.error("You're out of credits. Taking you to buy more…");
        setTimeout(() => navigate("/billing"), 1200);
        return;
      }
      if (!res.ok) throw new Error("Request failed");

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let headerParsed = false;
      let buffer = "";
      let pendingVideo = false;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        let chunk = decoder.decode(value, { stream: true });

        if (!headerParsed) {
          buffer += chunk;
          const nl = buffer.indexOf("\n");
          if (nl === -1) continue; // wait for full header line
          const header = buffer.slice(0, nl);
          if (header.startsWith("__CONVO__")) convoId = header.slice(9);
          chunk = buffer.slice(nl + 1);
          buffer = "";
          headerParsed = true;
        }

        if (chunk.includes("__POLL__")) {
          pendingVideo = true;
          chunk = chunk.replace("__POLL__", "");
        }

        setMessages((prev) => {
          const copy = [...prev];
          copy[copy.length - 1] = {
            ...copy[copy.length - 1],
            content: copy[copy.length - 1].content + chunk,
          };
          return copy;
        });
      }

      if (!activeId && convoId) setActiveId(convoId);
      loadConversations();
      refreshUser();

      if (pendingVideo && convoId) pollForVideo(convoId);
    } catch (e) {
      setMessages((prev) => {
        const copy = [...prev];
        copy[copy.length - 1] = {
          ...copy[copy.length - 1],
          content: copy[copy.length - 1].content || "⚠️ Failed to get a response. Please try again.",
        };
        return copy;
      });
      toast.error("Failed to send message");
    } finally {
      setBusy(false);
      setStreaming(false);
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <div className="h-screen flex bg-[#0A0A0A] overflow-hidden">
      {/* Sidebar desktop */}
      <div className="hidden md:block">
        <Sidebar
          conversations={conversations}
          activeId={activeId}
          onSelect={openConversation}
          onNew={newChat}
          onDelete={deleteConversation}
          onRename={renameConversation}
          onPin={togglePin}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      {/* Sidebar mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 md:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full">
            <Sidebar
              conversations={conversations}
              activeId={activeId}
              onSelect={openConversation}
              onNew={newChat}
              onDelete={deleteConversation}
              onRename={renameConversation}
              onPin={togglePin}
              onClose={() => setSidebarOpen(false)}
            />
          </div>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-white/10 flex items-center gap-3 px-4 nig-glass shrink-0 relative z-20">
          <button
            data-testid="open-sidebar-btn"
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-zinc-400 hover:text-white"
          >
            <Menu className="w-5 h-5" />
          </button>
          <ModelPicker models={models} value={model} onChange={setModel} />
          <div className="ml-auto flex items-center gap-2">
            {activeId && (
              <button
                data-testid="share-chat-btn"
                onClick={shareChat}
                title="Share this chat"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#141415] border border-white/10 hover:border-[#00FF66]/40 text-zinc-300 hover:text-white text-sm transition-colors"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Share</span>
              </button>
            )}
            <button
              data-testid="header-credits"
              onClick={() => navigate("/billing")}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[#141415] border border-white/10 hover:border-[#00FF66]/40 transition-colors"
            >
              <Coins className="w-4 h-4 text-[#00FF66]" />
              <span className="text-sm text-white font-medium">
                {user?.unlimited_credits ? "∞" : (user?.credits ?? 0).toLocaleString()}
              </span>
            </button>
            <button
              data-testid="header-buy-credits"
              onClick={() => navigate("/billing")}
              className="hidden sm:block px-3 py-1.5 rounded-full bg-[#00FF66] hover:bg-[#00CC52] text-black text-sm font-semibold transition-colors shadow-[0_0_24px_rgba(0,255,102,0.25)]"
            >
              Buy credits
            </button>
          </div>
        </header>

        <div ref={scrollRef} className="flex-1 overflow-y-auto relative">
          <div className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] blur-[120px] opacity-20" style={{ background: "radial-gradient(circle, #00FF66 0%, transparent 70%)" }} />
          {isEmpty ? (
            <div className="relative h-full flex flex-col items-center justify-center px-4 max-w-3xl mx-auto">
              <div className="relative mb-6">
                <div className="absolute inset-0 blur-2xl opacity-60" style={{ background: "radial-gradient(circle, #00FF66 0%, transparent 70%)" }} />
                <div className="relative w-16 h-16 rounded-2xl overflow-hidden shadow-[0_0_40px_rgba(0,255,102,0.4)] animate-float ring-1 ring-[#00FF66]/50">
                  <img src={ASSETS.logoMark} alt="NIGAMAI" className="w-full h-full object-cover" />
                </div>
              </div>
              <h1 className="font-heading font-extrabold text-4xl sm:text-5xl tracking-tighter text-white text-center">
                What are we <span className="text-[#00FF66] text-glow">building</span> today?
              </h1>
              <p className="text-zinc-500 mt-3 text-center">
                Your all-in-one coding assistant. Websites, apps, agents & anything code.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-10 w-full">
                {SUGGESTIONS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`suggestion-${i}`}
                    onClick={() => sendMessage(s.prompt, [])}
                    className="flex items-center gap-3 p-4 rounded-xl bg-[#141415] border border-white/10 hover:border-[#00FF66]/40 hover:bg-[#1F1F22] hover:shadow-[0_0_30px_rgba(0,255,102,0.1)] text-left transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-white/5 flex items-center justify-center group-hover:bg-[#00FF66]/10 transition-colors shrink-0">
                      <s.icon className="w-4 h-4 text-[#00FF66]" />
                    </div>
                    <span className="text-sm text-zinc-300">{s.title}</span>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="relative max-w-4xl mx-auto px-4 py-8 space-y-8">
              {messages.map((m, i) => (
                <ChatMessage
                  key={i}
                  role={m.role}
                  content={m.content}
                  images={m.images}
                  docNames={m.doc_names}
                  streaming={streaming && i === messages.length - 1 && m.role === "assistant"}
                />
              ))}
            </div>
          )}
        </div>

        {user && !user.unlimited_credits && (user.credits ?? 0) <= 10 && (
          <div data-testid="low-credit-banner" className="mx-4 mb-2 flex items-center gap-3 rounded-xl border border-[#00FF66]/30 bg-[#101a12] px-4 py-2.5">
            <Coins className="w-4 h-4 text-[#00FF66] shrink-0" />
            <span className="text-sm text-zinc-200 flex-1">
              You're low on credits (<span className="text-[#00FF66] font-semibold">{user.credits}</span> left). Buy more, or claim free daily credits tomorrow.
            </span>
            <button
              data-testid="low-credit-buy"
              onClick={() => navigate("/billing")}
              className="text-sm font-semibold text-black bg-[#00FF66] hover:bg-[#00CC52] px-3 py-1.5 rounded-lg transition-colors shrink-0"
            >
              Buy credits
            </button>
          </div>
        )}
        <ChatInput onSend={sendMessage} busy={busy} />
      </div>

      {showTour && <OnboardingTour onClose={closeTour} />}
    </div>
  );
}
